import { setOpenVessel } from '../lib/openVessel'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { setActiveClientId, setViewer } from '../lib/api'
import { trackLogin, setTrackConfig } from '../lib/track'
import { featureOn } from '../lib/features'
import { defaultLevel } from '../lib/permModules'
import { setShipTz } from '../lib/shipTime'
import { supabase, getClientConfig } from '../lib/supabase'
import { offline, setOfflineRights, startOfflineSync, syncNow } from '../lib/offline'
import { refreshOfflinePack, wipeOfflinePack } from '../lib/manualOffline'
import { isSimple, SIMPLE_PERMS, SIMPLE_FEATURES } from '../service/simpleMode'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [config, setConfig] = useState(null)
  const [selectedVessel, setSelectedVessel] = useState(null) // company drilling into a vessel
  const [loading, setLoading] = useState(true)
  // whose settings have finished loading: right after a login the user is known before the settings are,
  // and the shell would show the defaults ("My Vessel" / "Captain") to a crew login until they arrive
  const [configUid, setConfigUid] = useState(null)
  const lastUid = useRef(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) loadConfig(session.user.id)
      else setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // signed out (here or elsewhere): this login's offline data goes (OFFLINE-PLAN.md section 5)
      if (_event === 'SIGNED_OUT' && lastUid.current) { offline.wipe(lastUid.current).catch(() => {}); wipeOfflinePack(lastUid.current); lastUid.current = null }
      if (session?.user) lastUid.current = session.user.id
      setUser(session?.user ?? null)
      if (session?.user) loadConfig(session.user.id)
      else { setConfig(null); setConfigUid(null); setLoading(false) }
    })
    return () => subscription.unsubscribe()
  }, [])

  async function loadConfig(uid) {
    try {
      const cfg = await getClientConfig()
      setShipTz(cfg?.timezone || null)
      setConfig(cfg)
      // offline mode: the rights the cache was made with - changed rights drop the cached data; then send what waits
      if (cfg) { lastUid.current = uid; await offline.checkRights(uid, rightsHash(cfg)).catch(() => {}); startOfflineSync(); syncNow() }
      // the manuals offline pack (src/lib/manualOffline.js): refreshed in the background, at most every 6 hours
      if (cfg) refreshOfflinePack({ uid, clientId: cfg.id, rightsHash: rightsHash(cfg) }).catch(() => {})
      if (cfg) { setTrackConfig(cfg); trackLogin(cfg) }
    } catch (e) {
      console.error('Config load failed', e)
    } finally {
      setConfigUid(uid)
      setLoading(false)
    }
  }

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  async function signOut() {
    // logging out wipes this login's offline data - ask first when changes have not synced yet
    const uid = user && user.id
    if (uid) {
      const st = await offline.status(uid).catch(() => null)
      const n = st ? st.waiting.length + st.conflicts.length : 0
      if (n && !window.confirm(n + (n === 1 ? ' change has' : ' changes have') + ' not synced yet (no connection). Log out anyway and lose ' + (n === 1 ? 'it' : 'them') + '?')) return
      await offline.wipe(uid).catch(() => {})
      await wipeOfflinePack(uid)
    }
    lastUid.current = null
    // this device only: the same login on a phone / another browser stays logged in (the default 'global' ended them all)
    await supabase.auth.signOut({ scope: 'local' })
    setShipTz(null)
    setConfig(null)
    setConfigUid(null)
  }

  // When a company account drills into a vessel, that vessel becomes the effective config
  // so all existing per-vessel pages work unchanged.
  // a company member (fleet-4) keeps their own rights inside the vessel: member, memberLabel, permissions
  const member = config?.member || null
  const effectiveConfig = selectedVessel ? { ...selectedVessel, accountType: 'vessel', role: 'captain', _companyView: true, companyId: config?.companyId, companyName: config?.companyName, fleet: config?.fleet,
    ...(member ? { member, memberLabel: config.memberLabel, permissions: config.permissions } : {}), ...(config?.service ? { service: config.service } : {}) } : config
  // Keep fetchRows (supaFetch) scoped to the vessel currently being viewed.
  if (typeof setActiveClientId === 'function') setActiveClientId(effectiveConfig?.id || null)
  // the vessel open in the app, for the server (X-HC-Vessel on every /api/ call): never the company itself
  setOpenVessel(effectiveConfig)
  // whose personal data the pages may show (a company viewing a vessel: the whole crew, like the Captain)
  if (typeof setViewer === 'function') setViewer(effectiveConfig)
  const role = config?.role || null
  const accountType = config?.accountType || 'vessel'
  const isCrew = accountType === 'crew'
  const isCaptain = role === 'captain'
  // still loading while a logged-in user's settings are not there yet (a token refresh of the same login does not count)
  const busy = loading || (!!user && configUid !== user.id)

  // Permission check: can(module, level)
  // Captains always have full access
  // Crew check their permissions object
  // Levels: none < read < write < full
  const LEVELS = { none: 0, read: 1, write: 2, full: 3 }
  // Service package: a simple-mode vessel has no such module; a technician has only their jobs. No package: neither line does anything.
  const simple = isSimple(effectiveConfig?.service, effectiveConfig?.id)
  function can(module, level) {
    if (accountType === 'technician') return module === 'service_jobs'
    if (module === 'service_jobs') return false
    if (simple && SIMPLE_PERMS.includes(module)) return false
    if (member) return LEVELS[(config.permissions || {})[module] || 'none'] >= LEVELS[level]   // a company member: their area rights (src/lib/companyMembers.js)
    if (!isCrew) return true // captain has full access
    const perms = config?.permissions || {}
    const granted = perms[module] || defaultLevel(module) // default read if not set; APA / crew personal data hidden unless given
    return LEVELS[granted] >= LEVELS[level]
  }

  // Feature gating: is a module included in this vessel's plan (+ overrides)? A company looking at a vessel: that vessel's plan
  function hasFeature(module) {
    return featureOn(config, selectedVessel, module) && !(simple && SIMPLE_FEATURES.includes(module))   // Service package, simple mode
  }
  // offline mode: what this login may queue offline (the database checks again when it is sent)
  setOfflineRights({ can, isCrew, crewName: config?.crewName || '', vesselId: effectiveConfig?.id || null })

  return (
    <AuthContext.Provider value={{ user, config: effectiveConfig, rawConfig: config, selectedVessel, setSelectedVessel, loading: busy, signIn, signOut, role, accountType, isCrew, isCaptain, can, hasFeature }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

// the login's rights, as one string: when it changes, the offline cache made with the old rights is dropped
export function rightsHash(cfg) {
  const p = cfg && cfg.permissions ? Object.keys(cfg.permissions).sort().map(k => k + ':' + cfg.permissions[k]).join(',') : ''
  return [cfg && cfg.accountType, cfg && cfg.role, cfg && cfg.id, cfg && cfg.crewId, p].join('|')
}
